import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ArcGISSearchMap } from "../components/ArcGISSearchMap";
import {
  fetchListings,
  fetchMunicipios,
  PublicListing,
  MunicipioOption,
} from "../api/publicApi";

// Replace with the actual Power Pages solicitud form URL
const SOLICITUD_URL = "https://terrenos.powerappsportals.com/solicitud";

// ─── Compact listing row for the right panel ──────────────────────

const ListingRow: React.FC<{
  listing: PublicListing;
  selected: boolean;
  onClick: () => void;
}> = ({ listing, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-4 py-3 transition ${
      selected
        ? "bg-blue-50 border-l-4 border-l-blue-600"
        : "border-l-4 border-l-transparent hover:bg-gray-50"
    }`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="font-semibold text-gray-900 text-sm">{listing.subParcelId || "—"}</div>
        <div className="text-xs text-gray-500 truncate">{listing.municipio}</div>
        {listing.fincaId && (
          <div className="text-xs text-gray-400">Finca {listing.fincaId}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        {listing.canon != null && (
          <div className="text-sm font-semibold text-green-700">
            ${listing.canon.toLocaleString()}<span className="text-xs font-normal text-gray-400">/mes</span>
          </div>
        )}
        {listing.cabida != null && (
          <div className="text-xs text-gray-400">{listing.cabida.toFixed(2)} cdas</div>
        )}
      </div>
    </div>
  </button>
);

// ─── Main page ────────────────────────────────────────────────────

export const PublicListingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [allListings, setAllListings] = useState<PublicListing[]>([]);
  const [listings, setListings] = useState<PublicListing[]>([]);
  const [municipios, setMunicipios] = useState<MunicipioOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedListing, setSelectedListing] = useState<PublicListing | null>(null);

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [municipio, setMunicipio] = useState<number | "">(
    searchParams.get("municipio") ? Number(searchParams.get("municipio")) : ""
  );

  useEffect(() => {
    fetchMunicipios().then(setMunicipios).catch(() => {});
  }, []);

  // Re-fetch only when municipio changes — search is handled client-side
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedListing(null);
    try {
      const data = await fetchListings({
        municipio: municipio !== "" ? municipio : undefined,
        top: 200,
      });
      setAllListings(data.value);
    } catch {
      setError("No se pudo conectar con el servidor. Intente de nuevo.");
    } finally {
      setLoading(false);
    }
  }, [municipio]);

  useEffect(() => {
    load();
    const params: Record<string, string> = {};
    if (municipio !== "") params.municipio = String(municipio);
    setSearchParams(params, { replace: true });
  }, [municipio, load, setSearchParams]);

  // Client-side wildcard filter — runs instantly on every keystroke
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) { setListings(allListings); return; }
    setListings(allListings.filter(l =>
      (l.subParcelId || "").toLowerCase().includes(q) ||
      l.municipio.toLowerCase().includes(q) ||
      (l.acquisitionName || "").toLowerCase().includes(q) ||
      (l.uso || "").toLowerCase().includes(q) ||
      (l.catastro || "").toLowerCase().includes(q) ||
      (l.fincaId || "").toLowerCase().includes(q)
    ));
  }, [search, allListings]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault(); // filter effect already runs on every keystroke
  };

  const handleMapClick = (parcelId: string) => {
    const found = listings.find((l) => l.parcelId === parcelId);
    if (found) setSelectedListing((prev) => (prev?.id === found.id ? null : found));
  };

  const parcelIds = listings.map((l) => l.parcelId).filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-brand-800 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <p className="text-blue-200 text-sm font-medium uppercase tracking-widest mb-1">
            Administración de Terrenos · Puerto Rico
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">
            Terrenos en Arrendamiento
          </h1>
          <p className="text-blue-200 text-sm max-w-xl">
            Parcelas y sub-parcelas del gobierno disponibles para arrendamiento.
          </p>

          <form onSubmit={handleSearchSubmit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Buscar por número de sub-parcela..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-3 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
            <select
              value={municipio}
              onChange={(e) =>
                setMunicipio(e.target.value === "" ? "" : Number(e.target.value))
              }
              className="py-3 px-4 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
            >
              <option value="">Todos los municipios</option>
              {municipios.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.name} ({m.count})
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="px-6 py-3 bg-white text-blue-700 font-semibold rounded-xl hover:bg-blue-50 transition text-sm"
            >
              Buscar
            </button>
          </form>
        </div>
      </div>

      {/* Map + List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
            <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Cargando terrenos disponibles…</span>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-500">
            <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm">{error}</p>
            <button onClick={load} className="text-sm text-blue-600 hover:underline">
              Reintentar
            </button>
          </div>
        )}

        {/* Map + list side by side */}
        {!loading && !error && (
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Map — left 3/5 */}
            <div className="lg:col-span-3">
              <ArcGISSearchMap
                parcelIds={parcelIds}
                onParcelClick={handleMapClick}
                height="600px"
                selectedParcelId={selectedListing?.parcelId}
              />
            </div>

            {/* Right panel — 2/5 */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              {/* Selected listing card */}
              {selectedListing && (
                <div className="bg-white rounded-xl border-2 border-blue-500 shadow-md p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">
                        Sub-Parcela seleccionada
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {selectedListing.subParcelId || "—"}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedListing(null)}
                      className="text-gray-400 hover:text-gray-600 text-2xl leading-none mt-0.5"
                      aria-label="Cerrar"
                    >
                      ×
                    </button>
                  </div>

                  <div className="text-sm text-gray-600 mb-0.5">{selectedListing.municipio}</div>
                  {selectedListing.fincaId && (
                    <div className="text-xs text-gray-400 mb-3">Finca {selectedListing.fincaId}</div>
                  )}

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {selectedListing.cabida != null && (
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <p className="text-xs text-gray-400 mb-0.5">Cabida</p>
                        <p className="text-sm font-semibold">{selectedListing.cabida.toFixed(2)} cdas</p>
                      </div>
                    )}
                    {selectedListing.canon != null && (
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <p className="text-xs text-gray-400 mb-0.5">Canon mensual</p>
                        <p className="text-sm font-semibold text-green-700">
                          ${selectedListing.canon.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {selectedListing.uso && (
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <p className="text-xs text-gray-400 mb-0.5">Uso</p>
                        <p className="text-sm font-semibold truncate">{selectedListing.uso}</p>
                      </div>
                    )}
                    {selectedListing.zoning && (
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <p className="text-xs text-gray-400 mb-0.5">Zonificación</p>
                        <p className="text-sm font-semibold">{selectedListing.zoning}</p>
                      </div>
                    )}
                  </div>

                  <a
                    href={`${SOLICITUD_URL}?parcela=${encodeURIComponent(
                      selectedListing.subParcelId || selectedListing.id
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition text-sm"
                  >
                    Solicitar más información
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              )}

              {/* Listing list */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex-1">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {listings.length === 0
                      ? "No se encontraron terrenos"
                      : `${listings.length} terreno${listings.length !== 1 ? "s" : ""} disponible${listings.length !== 1 ? "s" : ""}`}
                  </span>
                  {(search || municipio !== "") && (
                    <button
                      onClick={() => { setSearch(""); setMunicipio(""); }}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>

                <div className="divide-y divide-gray-100 max-h-[540px] overflow-y-auto">
                  {listings.length === 0 ? (
                    <div className="p-10 text-center text-gray-400 text-sm">
                      No hay terrenos disponibles con estos filtros.
                    </div>
                  ) : (
                    listings.map((l) => (
                      <ListingRow
                        key={l.id}
                        listing={l}
                        selected={selectedListing?.id === l.id}
                        onClick={() =>
                          setSelectedListing((prev) => (prev?.id === l.id ? null : l))
                        }
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
